import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";

import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import SearchBar from "./components/SearchBar";
import AddEmployeeForm from "./components/AddEmployeeForm";
import EmployeeList from "./components/EmployeeList";
import Home from "./components/dashboard/Home";
import { SkeletonList } from "./components/ui/Skeleton";
import { useToast } from "./hooks/useToast";

import "./index.css";
import "./styles/toast.css";

function App() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1024;
    }
    return false;
  });

  const [activeSection, setActiveSection] = useState("home");
  const [searchTerm, setSearchTerm] = useState("");

  const { showSuccess, showError } = useToast();

  /* ------------------ FETCH EMPLOYEES ------------------ */

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await fetch(
          "https://jsonplaceholder.typicode.com/users"
        );

        if (!res.ok) throw new Error("Failed to fetch employees");

        const data = await res.json();
        setEmployees(data);
        setFilteredEmployees(data);
      } catch (err) {
        setError(err.message);
        showError(`Failed to load employees: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
    // eslint-disable-next-line
  }, []);

  /* ------------------ SIDEBAR RESPONSIVE ------------------ */

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ------------------ SEARCH ------------------ */

  const handleSearch = (query) => {
    setSearchTerm(query);

    const filtered = employees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(query.toLowerCase()) ||
        emp.email.toLowerCase().includes(query.toLowerCase())
    );

    setFilteredEmployees(filtered);
  };

  /* ------------------ CRUD ------------------ */

  const addEmployee = (employee) => {
    setEmployees((prev) => [employee, ...prev]);
    setFilteredEmployees((prev) => [employee, ...prev]);
  };

  const deleteEmployee = async (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    const name = employee?.name || "Employee";

    setEmployees((prev) => prev.filter((e) => e.id !== employeeId));
    setFilteredEmployees((prev) =>
      prev.filter((e) => e.id !== employeeId)
    );

    showSuccess(`${name} deleted successfully`);

    try {
      await fetch(
        `https://jsonplaceholder.typicode.com/users/${employeeId}`,
        { method: "DELETE" }
      );
    } catch (err) {
      showError(`Failed to delete ${name}`);

      const data = await fetch(
        "https://jsonplaceholder.typicode.com/users"
      ).then((r) => r.json());

      setEmployees(data);
      setFilteredEmployees(data);
    }
  };

  const updateEmployee = async (employeeId, updatedData) => {
    const employee = employees.find((e) => e.id === employeeId);
    const name = employee?.name || "Employee";

    const updated = employees.map((emp) =>
      emp.id === employeeId ? { ...emp, ...updatedData } : emp
    );

    setEmployees(updated);
    setFilteredEmployees(updated);

    showSuccess(`${name} updated successfully`);

    try {
      await fetch(
        `https://jsonplaceholder.typicode.com/users/${employeeId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedData),
        }
      );
    } catch {
      showError(`Failed to update ${name}`);
    }
  };

  /* ------------------ CONTENT ROUTER ------------------ */

  const renderContent = () => {
    switch (activeSection) {
      case "home":
        return (
          <Home
            employees={employees}
            onAddEmployee={() => setActiveSection("add")}
            onViewEmployees={() =>
              setActiveSection("employees")
            }
            onExportCSV={() => {}}
          />
        );

      case "add":
        return <AddEmployeeForm onAdd={addEmployee} />;

      case "search":
      case "employees":
      default:
        return (
          <>
            <SearchBar onSearch={handleSearch} />

            {loading ? (
              <SkeletonList count={6} />
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 dark:text-red-400 font-medium">
                  {error}
                </p>
              </div>
            ) : (
              <EmployeeList
                employees={filteredEmployees}
                searchTerm={searchTerm}
                onDelete={deleteEmployee}
                onUpdate={updateEmployee}
              />
            )}
          </>
        );
    }
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
        <div className="flex">
          {/* Sidebar */}
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />

          {/* Main */}
          <div className="flex-1 flex flex-col">
            <Header
              onMenuClick={() =>
                setSidebarOpen(!sidebarOpen)
              }
              employeeCount={employees.length}
            />

            <main className="flex-1 p-4 lg:p-6 overflow-auto">
              <div className="max-w-7xl mx-auto">
                {renderContent()}
              </div>
            </main>
          </div>
        </div>

        {/* 🔥 SAAS GRADE TOASTER */}
        <Toaster
          position="bottom-left"
          toastOptions={{
            classNames: {
              toast:
                "toast-base toast-sidebar backdrop-blur rounded-xl shadow-xl",

              success:
                "toast-success bg-gradient-to-r from-emerald-500 to-green-600 text-white border-l-4 border-green-300",

              error:
                "toast-error bg-gradient-to-r from-red-500 to-rose-600 text-white border-l-4 border-red-300",

              warning:
                "toast-warning bg-amber-400 text-gray-900",
            },
          }}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
